import DayLengthChart from "@/components/DayLengthChart";

export function GrainOverlay() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 opacity-[0.08] mix-blend-multiply"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")",
      }}
    />
  );
}

export function PageHeader() {
  return (
    <header className="mb-16">
      <p className="font-sans text-[0.72rem] uppercase tracking-[0.4em] text-[#8a6f52]">A clock aligned with the sun</p>
      <h1 className="mt-5 font-serif text-[2.8rem] leading-[1.05] md:text-[4.2rem] md:leading-[1.02]">
        True Solar <em className="italic text-[#c0501b]">Time</em>
      </h1>
      <p className="mt-6 max-w-xl text-lg leading-relaxed text-[#5e4a35] md:text-xl">
        Your watch tells civil time — stitched to a time-zone, nudged by politics. The sun keeps its own time.
        This is where it is, right now, where you are.
      </p>
    </header>
  );
}

export function DaylightSection({ now, lat, placeLabel }: { now: Date; lat: number; placeLabel: string }) {
  return (
    <section className="mt-20 border-t border-[#d9c9ad] pt-10">
      <p className="font-sans text-[0.7rem] uppercase tracking-[0.4em] text-[#8a6f52]">Light through the year</p>
      <h3 className="mt-1 font-serif text-2xl md:text-3xl">Hours of daylight</h3>
      <p className="mt-3 max-w-2xl text-[#5e4a35]">How long the sun stays up each day at {placeLabel}.</p>
      <div className="mt-8">
        <DayLengthChart now={now} latitude={lat} />
      </div>
    </section>
  );
}

const FORMULA = `True Solar Time
  = Local Clock Time
  + Equation of Time
  + Longitude Correction

Longitude Correction
  = 4 × (longitude − standard meridian)`;

export function Explanation() {
  return (
    <section className="mt-20 grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-16">
      <div>
        <h3 className="font-serif text-2xl">The formula</h3>
        <p className="mt-4 leading-relaxed text-[#5e4a35]">
          True solar time is simply your clock time, corrected for two things: where the sun actually is in its
          yearly dance, and where you stand within your time-zone.
        </p>
        <pre className="mt-5 overflow-x-auto rounded-sm border border-[#d9c9ad] bg-[#fbf6ea] px-5 py-4 font-sans text-sm leading-relaxed text-[#2b1f14]">
          {FORMULA}
        </pre>
      </div>
      <div>
        <h3 className="font-serif text-2xl">Why bother?</h3>
        <p className="mt-4 leading-relaxed text-[#5e4a35]">
          Clock time is a convenience — a single number stretched across a whole slice of the planet. Your body,
          the plants, the birds, the rising light — none of them read a clock. They listen to the sun. Setting
          your rhythm by true solar time is a small act of returning to the real world.
        </p>
        <p className="mt-4 leading-relaxed text-[#5e4a35]">
          Every calculation happens in your browser. Only two things ever leave your device:
        </p>
        <ul data-testid="solar-privacy-list" className="mt-3 list-disc space-y-1 pl-5 leading-relaxed text-[#5e4a35]">
          <li>the place names you search for, sent to OpenStreetMap to look the place up;</li>
          <li>map tiles, loaded from OpenStreetMap when you open the map.</li>
        </ul>
        <p className="mt-3 leading-relaxed text-[#5e4a35]">
          Your exact location from "Use my location" stays on your device. The last place you chose is
          remembered only in this browser. No accounts, no analytics, no tracking.
        </p>
      </div>
    </section>
  );
}

export function PageFooter({ tzLabel }: { tzLabel: string }) {
  return (
    <footer className="mt-24 flex items-center justify-between border-t border-[#d9c9ad] pt-6 font-sans text-xs uppercase tracking-widest text-[#8a6f52]">
      <span data-testid="solar-footer-privacy">Calculated on your device · no tracking</span>
      <span className="tabular-nums">{tzLabel}</span>
    </footer>
  );
}
